import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { buildGameImportProgressItems, setImportProgressStatus } from "../nestifyz/buildGameImportProgressItems";
import { GameImportCoverUpload } from "../nestifyz/GameImportCoverUpload";
import { GameImportGameTopicsUpload } from "../nestifyz/GameImportGameTopicsUpload";
import { GameImportPlayableLink } from "../nestifyz/GameImportPlayableLink";
import { GameImportPlayTestUpload } from "../nestifyz/GameImportPlaytestUpload";
import { GameImportProgressChecklist } from "../nestifyz/GameImportProgressChecklist";
import { ImportProgressList } from "./ImportProgressList";
import { GameImportRulebookUpload } from "../nestifyz/GameImportRulebookUpload";
import { getImportablePlaytests } from "../nestifyz/playtestImportUtils";
import { hasImportableComponentsTopic, hasImportableOverviewTopic } from "../nestifyz/gameTopicImportUtils";
import { getRulebooksForImport } from "../nestifyz/rulebookImportUtils";
import { GameImportMediaUpload } from "./GameImportMediaUpload";

/**
 * @typedef {'link'|'cover'|'gameTopics'|'rulebooks'|'tasks'|'media'|'playtests'} GameImportPhase
 */

/**
 * @param {import("../nestifyz/parseExport").NestiFyzGame} nestifyzGame
 * @returns {GameImportPhase[]}
 */
const buildPhases = (nestifyzGame) => {
  /** @type {GameImportPhase[]} */
  const phases = [];

  if (nestifyzGame.playableDigitalVersionLink) {
    phases.push("link");
  }
  if (nestifyzGame.coverFile) {
    phases.push("cover");
  }
  if (hasImportableOverviewTopic(nestifyzGame) || hasImportableComponentsTopic(nestifyzGame)) {
    phases.push("gameTopics");
  }
  if (getRulebooksForImport(nestifyzGame.rulebooks).length > 0) {
    phases.push("rulebooks");
  }
  if ((nestifyzGame.progressChecklist || []).some((item) => item.item)) {
    phases.push("tasks");
  }
  if ((nestifyzGame.mediaFiles || []).length > 0) {
    phases.push("media");
  }
  if (getImportablePlaytests(nestifyzGame.playtests).length > 0) {
    phases.push("playtests");
  }

  return phases;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("../nestifyz/parseExport").NestiFyzGame} props.nestifyzGame
 * @param {() => void} [props.onComplete]
 */
export const GameImportPart2 = ({ gameId, nestifyzGame, onComplete }) => {
  const phases = useMemo(() => buildPhases(nestifyzGame), [nestifyzGame]);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [progressItems, setProgressItems] = useState(() => buildGameImportProgressItems(nestifyzGame));
  const [hasFailed, setHasFailed] = useState(false);

  const currentPhase = phases[phaseIndex];

  const updateProgress = useCallback((id, status) => {
    setProgressItems((items) => setImportProgressStatus(items, id, status));
  }, []);

  const advancePhase = useCallback(() => {
    if (phaseIndex + 1 >= phases.length) {
      onComplete?.();
      return;
    }

    setPhaseIndex((index) => index + 1);
  }, [onComplete, phaseIndex, phases.length]);

  const handlePhaseError = useCallback(() => {
    if (!hasFailed) {
      setHasFailed(true);
    }
  }, [hasFailed]);

  const checklistItems = useMemo(
    () => (nestifyzGame.progressChecklist || []).filter((item) => item.item),
    [nestifyzGame.progressChecklist]
  );

  useEffect(() => {
    if (phases.length === 0) {
      onComplete?.();
    }
  }, [onComplete, phases.length]);

  if (phases.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <ImportProgressList items={progressItems} />
      {currentPhase === "link" && nestifyzGame.playableDigitalVersionLink && (
        <GameImportPlayableLink
          gameId={gameId}
          url={nestifyzGame.playableDigitalVersionLink}
          onStart={() => {
            updateProgress("link", "in_progress");
          }}
          onComplete={() => {
            updateProgress("link", "complete");
            advancePhase();
          }}
          onError={() => {
            updateProgress("link", "error");
            handlePhaseError();
          }}
        />
      )}
      {currentPhase === "cover" && nestifyzGame.coverFile && (
        <GameImportCoverUpload
          gameId={gameId}
          coverFile={nestifyzGame.coverFile}
          onStart={() => {
            updateProgress("cover", "in_progress");
          }}
          onComplete={() => {
            updateProgress("cover", "complete");
            advancePhase();
          }}
          onError={() => {
            updateProgress("cover", "error");
            handlePhaseError();
          }}
        />
      )}
      {currentPhase === "tasks" && (
        <GameImportProgressChecklist
          gameId={gameId}
          items={checklistItems}
          onItemStart={(index) => {
            updateProgress(`task-${index}`, "in_progress");
          }}
          onItemComplete={(index) => {
            updateProgress(`task-${index}`, "complete");
          }}
          onItemError={(index) => {
            updateProgress(`task-${index}`, "error");
          }}
          onComplete={advancePhase}
          onError={handlePhaseError}
        />
      )}
      {currentPhase === "gameTopics" && (
        <GameImportGameTopicsUpload
          gameId={gameId}
          nestifyzGame={nestifyzGame}
          onItemStart={(kind) => {
            updateProgress(`game-topic-${kind}`, "in_progress");
          }}
          onItemComplete={(kind) => {
            updateProgress(`game-topic-${kind}`, "complete");
          }}
          onItemError={(kind) => {
            updateProgress(`game-topic-${kind}`, "error");
          }}
          onComplete={advancePhase}
          onError={handlePhaseError}
        />
      )}
      {currentPhase === "rulebooks" && (
        <GameImportRulebookUpload
          gameId={gameId}
          rulebooks={nestifyzGame.rulebooks}
          onItemStart={(index) => {
            updateProgress(`rulebook-${index}`, "in_progress");
          }}
          onItemComplete={(index) => {
            updateProgress(`rulebook-${index}`, "complete");
          }}
          onItemSkip={(index) => {
            updateProgress(`rulebook-${index}`, "skipped");
          }}
          onItemError={(index) => {
            updateProgress(`rulebook-${index}`, "error");
          }}
          onTopicStart={(index) => {
            updateProgress(`rulebook-${index}-topic`, "in_progress");
          }}
          onTopicComplete={(index) => {
            updateProgress(`rulebook-${index}-topic`, "complete");
          }}
          onTopicError={(index) => {
            updateProgress(`rulebook-${index}-topic`, "error");
          }}
          onIllustrationStart={(rulebookIndex, illustrationIndex) => {
            updateProgress(`rulebook-${rulebookIndex}-illustration-${illustrationIndex}`, "in_progress");
          }}
          onIllustrationComplete={(rulebookIndex, illustrationIndex) => {
            updateProgress(`rulebook-${rulebookIndex}-illustration-${illustrationIndex}`, "complete");
          }}
          onIllustrationSkip={(rulebookIndex, illustrationIndex) => {
            updateProgress(`rulebook-${rulebookIndex}-illustration-${illustrationIndex}`, "skipped");
          }}
          onIllustrationError={(rulebookIndex, illustrationIndex) => {
            updateProgress(`rulebook-${rulebookIndex}-illustration-${illustrationIndex}`, "error");
          }}
          onComplete={advancePhase}
          onError={handlePhaseError}
        />
      )}
      {currentPhase === "media" && (nestifyzGame.mediaFiles || []).length > 0 && (
        <GameImportMediaUpload
          gameId={gameId}
          files={nestifyzGame.mediaFiles || []}
          onItemStart={(index) => {
            updateProgress(`media-${index}`, "in_progress");
          }}
          onItemComplete={(index) => {
            updateProgress(`media-${index}`, "complete");
          }}
          onItemSkip={(index) => {
            updateProgress(`media-${index}`, "skipped");
          }}
          onItemError={(index) => {
            updateProgress(`media-${index}`, "error");
          }}
          onComplete={advancePhase}
          onError={handlePhaseError}
        />
      )}
      {currentPhase === "playtests" && (
        <GameImportPlayTestUpload
          gameId={gameId}
          nestifyzGame={nestifyzGame}
          onItemStart={(index) => {
            updateProgress(`playtest-${index}`, "in_progress");
          }}
          onItemComplete={(index) => {
            updateProgress(`playtest-${index}`, "complete");
          }}
          onItemError={(index) => {
            updateProgress(`playtest-${index}`, "error");
          }}
          onPhotoStart={(playtestIndex, photoIndex) => {
            updateProgress(`playtest-${playtestIndex}-photo-${photoIndex}`, "in_progress");
          }}
          onPhotoComplete={(playtestIndex, photoIndex) => {
            updateProgress(`playtest-${playtestIndex}-photo-${photoIndex}`, "complete");
          }}
          onPhotoSkip={(playtestIndex, photoIndex) => {
            updateProgress(`playtest-${playtestIndex}-photo-${photoIndex}`, "skipped");
          }}
          onPhotoError={(playtestIndex, photoIndex) => {
            updateProgress(`playtest-${playtestIndex}-photo-${photoIndex}`, "error");
          }}
          onComplete={advancePhase}
          onError={handlePhaseError}
        />
      )}
    </Box>
  );
};
